/**
 * Animator Control — Phase 2C : réglages des jeux à distance, pilotés par un SCHÉMA
 * déclaratif (un panneau générique unique, GameParamsPanel, rend n'importe quel jeu
 * à partir de son schéma). Le cockpit écrit les valeurs dans Firestore (gameParams) ;
 * l'écran les relaie au jeu (message 'set-game-params') qui les applique à son store.
 *
 * ⚠️ COUPLAGE DOCUMENTÉ (comme la liste GAMES, cf gotcha #5) : ces options recopient
 * les réglages INTÉGRÉS de chaque jeu. Sources de vérité côté jeux :
 *   geoloc/src/config/global.config.tsx + src/data/locations.ts ·
 *   petit-bac/src/config/global.tsx · estim/src/config/global.config.tsx ·
 *   draw-guessr/src/config/global.config.ts · footboared/src/config/global.config.ts (TUNING) ·
 *   unblind-test playlistsDeezer.data.tsx · untierce/src/config/global.config.tsx ·
 *   bomber-kitten/src/game/animatorParams.ts
 * Si un jeu ajoute/retire une option, METTRE À JOUR ici.
 * Dernière synchro : 2026-09-16 (contenu anglais UK/USA : playlists International,
 * thèmes geoloc us-cities/uk-cities/nfl-teams/premier-league, catégories pub Petit Bac).
 *
 * ⚠️ AUCUN LIBELLÉ EN DUR ici : les champs portent des CLÉS i18n, résolues par
 * GameParamsPanel avec `t()`. Le cockpit est ouvert par le client payant sur son
 * téléphone dès la première soirée — il doit parler la langue de l'établissement
 * (lancement anglo-saxon, septembre 2026). Les seuls libellés bruts autorisés sont
 * les noms propres non traduisibles (régions françaises, playlists IA du client).
 */

import {
    BLIND_TEST_PLAYLISTS,
    PROPOSAL_MODES,
    ANSWER_MODES,
    REFLECTION_TIMES,
    DEFAULT_BLIND_TEST_PARAMS,
    DEFAULT_INTL_PLAYLIST_ID,
    numTracksOptionsFor,
    numTracksOptionsForPlaylist,
    playlistAllowsWork,
    type AiPlaylistOption,
} from './blindTestParams.config';

/** Traduction : signature minimale de `t` (évite d'importer les types i18next ici). */
export type TFunc = (key: string, options?: Record<string, unknown>) => string;

export type ParamValue = string | number | boolean | string[];
export type ParamsMap = Record<string, ParamValue>;
/** Geste de l'animateur : `null` = remettre un réglage facultatif sur « Comme sur la TV » (clé retirée). */
export type ParamsPatch = Record<string, ParamValue | null>;

/**
 * Option d'un select / multiselect. `labelKey` (clé i18n) est la forme normale ;
 * `label` est le repli pour les libellés non traduisibles (nom propre, playlist
 * créée par le client). `groupKey` regroupe les options sous un `<optgroup>`.
 */
export type SelectOption = {
    value: string;
    labelKey?: string;
    label?: string;
    groupKey?: string;
    /** Pastille « Nouveau » sur l'option jusqu'à cette date (ms, UTC), puis plus rien. */
    newUntil?: number;
    /**
     * Option impossible avec les autres réglages : affichée grisée avec sa raison
     * (`reasonKey`, clé i18n), et la ligne repliée dit ce qui est joué à la place
     * (`summaryKey`) si c'est le choix courant. JAMAIS retirée de la liste : retirée, la
     * re-validation effacerait le choix mémorisé de l'animateur (Blind Test : « L'œuvre »
     * sur une playlist sans films, que la TV joue en « L'artiste » sans oublier le choix).
     */
    unavailable?: { reasonKey: string; summaryKey: string };
};

/** L'option porte encore sa pastille « Nouveau » ? */
export const isOptionNew = (option: SelectOption, now: number = Date.now()) =>
    option.newUntil !== undefined && now < option.newUntil;

/** Libellé affiché d'une option (clé i18n si présente, sinon libellé brut). */
export function optionLabel(option: SelectOption, t: TFunc): string {
    return option.labelKey ? t(option.labelKey) : (option.label ?? option.value);
}

/** Champ « chips » : un nombre choisi parmi une liste (statique ou dépendante). */
export type ChipsField = {
    type: 'chips';
    key: string;
    labelKey: string;
    options: number[] | ((p: ParamsMap) => number[]);
    /** Affichage d'une valeur (ex. minutes, suffixe « s », « sans limite »). */
    format?: (v: number, t: TFunc) => string;
    /** Réglage facultatif : cf `SelectField.unsetLabelKey`. */
    unsetLabelKey?: string;
    /** Réglages dont dépendent les options (Blind Test : nombre de titres ← playlist). */
    dependsOn?: string[];
};

/** Champ « select » : une valeur string parmi une liste (statique ou dépendante). */
export type SelectField = {
    type: 'select';
    key: string;
    labelKey: string;
    options: SelectOption[] | ((p: ParamsMap) => SelectOption[]);
    /**
     * Réglage FACULTATIF : jamais écrit dans `gameParams` tant que l'animateur ne
     * l'a pas choisi sur son téléphone. Tant qu'il ne l'est pas, la ligne affiche ce
     * libellé (« Comme sur la TV ») et le jeu garde le choix fait sur l'écran ; la
     * liste propose aussi ce libellé pour y revenir (retire la clé). Pourquoi : le
     * panneau renvoie TOUT l'objet à chaque modification (gotcha #184) — avec une
     * valeur par défaut, un bar en « Réponse libre » sur la TV repasserait en
     * « 4 propositions » en changeant seulement la playlist au téléphone, et un bar
     * qui a choisi « Années 80 » sur la TV la perdrait en touchant au mode.
     * Une valeur dans `defaults` ne sert alors que de repli de re-validation (chips).
     */
    unsetLabelKey?: string;
};

/** Champ « toggle » : un booléen. */
export type ToggleField = {
    type: 'toggle';
    key: string;
    labelKey: string;
    labelOnKey: string;
    labelOffKey: string;
};

/** Champ « multiselect » : un sous-ensemble d'options (au moins `minSelected`). */
export type MultiselectField = {
    type: 'multiselect';
    key: string;
    labelKey: string;
    options: SelectOption[];
    minSelected: number;
};

export type ParamField = ChipsField | SelectField | ToggleField | MultiselectField;

export type GameParamsSchema = {
    gameID: string;
    /** Clé i18n du titre du panneau (« Réglages X »). */
    titleKey: string;
    /** Icône Lucide (clé) affichée dans l'entête du panneau. */
    icon: 'music' | 'map-pin' | 'pencil' | 'home' | 'goal' | 'sliders';
    fields: ParamField[];
    /** Valeurs par défaut (servent d'init et de fallback de re-validation). */
    defaults: ParamsMap;
    /** Masquer un champ selon les valeurs courantes (ex. geoloc : pas de niveau en thème NBA). */
    hideField?: (key: string, p: ParamsMap) => boolean;
    /** Carte d'info optionnelle (ex. Blind Test : création IA sur l'écran du jeu). */
    note?: { titleKey: string; subKey: string };
};

const P = 'animator.params';

/** Suffixe « s » / « min » / « buts » d'une valeur numérique, traduit. */
const seconds = (v: number, t: TFunc) => t(`${P}.seconds`, { value: v });

/**
 * Thèmes Geoloc — miroir de `DATASET` (geoloc/src/data/locations.ts), dans le même
 * ordre. `us-cities`, `uk-cities`, `nfl-teams` et `premier-league` sont le contenu
 * anglophone sorti le 16/09/2026 (geoloc#70).
 */
const GEOLOC_THEMES: SelectOption[] = [
    'world-cities',
    'drapeaux-du-monde',
    'silhouettes-du-monde',
    'paysages-du-monde',
    'world-monuments',
    'fr-cities',
    'us-cities',
    'uk-cities',
    'nba-cities',
    'football-cities',
    'nfl-teams',
    'premier-league',
    'world-cup-countries',
].map((value) => ({ value, labelKey: `${P}.geolocThemes.${value}` }));

const DIFFICULTY_4: SelectOption[] = ['easy', 'medium', 'hard', 'progressive'].map((value) => ({
    value,
    labelKey: `${P}.levels.${value}`,
}));

/**
 * Régions françaises et britanniques — miroir de `COUNTRIES` (estim). Ce sont des
 * NOMS PROPRES : le jeu ne les traduit pas non plus, seul le libellé « tout le
 * pays » change de langue.
 */
const ESTIM_REGIONS_FR: SelectOption[] = [
    { value: 'all', labelKey: `${P}.estim.allFrance` },
    { value: 'ile-de-france', label: 'Île-de-France' },
    { value: 'auvergne-rhone-alpes', label: 'Auvergne-Rhône-Alpes' },
    { value: 'provence-alpes-cote-dazur', label: "Provence-Alpes-Côte d'Azur" },
    { value: 'nouvelle-aquitaine', label: 'Nouvelle-Aquitaine' },
    { value: 'occitanie', label: 'Occitanie' },
    { value: 'bretagne', label: 'Bretagne' },
    { value: 'pays-de-la-loire', label: 'Pays de la Loire' },
    { value: 'hauts-de-france', label: 'Hauts-de-France' },
    { value: 'grand-est', label: 'Grand Est' },
    { value: 'normandie', label: 'Normandie' },
    { value: 'bourgogne-franche-comte', label: 'Bourgogne-Franche-Comté' },
    { value: 'centre-val-de-loire', label: 'Centre-Val de Loire' },
    { value: 'corse', label: 'Corse' },
];

const ESTIM_REGIONS_GB: SelectOption[] = [
    { value: 'all', labelKey: `${P}.estim.allUk` },
    { value: 'london', label: 'London' },
    { value: 'south-east', label: 'South East' },
    { value: 'south-west', label: 'South West' },
    { value: 'west-midlands', label: 'West Midlands' },
    { value: 'east-midlands', label: 'East Midlands' },
    { value: 'north-west', label: 'North West' },
    { value: 'yorkshire', label: 'Yorkshire' },
    { value: 'scotland', label: 'Scotland' },
    { value: 'wales', label: 'Wales' },
    { value: 'northern-ireland', label: 'Northern Ireland' },
    { value: 'east', label: 'East of England' },
    { value: 'north-east', label: 'North East' },
];

/**
 * Catégories du Petit Bac — miroir de `CATEGORIES` (petit-bac/src/config/global.tsx),
 * dans le même ordre. Les 5 dernières sont les catégories « pub » du lancement
 * anglophone (petit-bac#19).
 */
const PETIT_BAC_CATEGORIES: SelectOption[] = [
    'actrices',
    'anatomie',
    'animaux',
    'boissons',
    'capitales',
    'couleurs',
    'fruits&legumes',
    'instruments',
    'marques',
    'metiers',
    'pays',
    'prenoms-feminins',
    'prenoms-masculins',
    'sports',
    'vetements',
    'villes',
    'series',
    'equipes-sport',
    'plats',
    'chansons',
    'personnages',
].map((value) => ({ value, labelKey: `${P}.categories.${value}` }));

const DRAW_GUESSR_THEMES: SelectOption[] = [
    'melange',
    'halloween',
    'classique',
    'soiree',
    'cinema',
    'bouffe',
    'animaux',
    'quotidien',
    'sport',
].map((value) => ({ value, labelKey: `${P}.drawThemes.${value}` }));

const DRAW_DIFFICULTY: SelectOption[] = ['progressive', 'facile', 'moyen', 'difficile', 'toutes'].map((value) => ({
    value,
    labelKey: `${P}.drawDifficulty.${value}`,
}));

/** Arènes de Bomber Kitten — miroir de ANIMATOR_WORLDS (bomber-kitten/src/game/animatorParams.ts). */
const BOMBER_WORLDS: SelectOption[] = ['random', 'forest', 'ice', 'fire'].map((value) => ({
    value,
    labelKey: `${P}.bomberWorlds.${value}`,
}));

/** Playlists intégrées, groupées par famille (comme le sélecteur du jeu). */
const playlistOption = (p: (typeof BLIND_TEST_PLAYLISTS)[number]): SelectOption => ({
    value: p.id,
    labelKey: `${P}.playlists.${p.nameKey}`,
    groupKey: p.region === 'intl' ? `${P}.groupIntl` : `${P}.groupFr`,
});

/** « en-GB », « en_US » → « en » (la console envoie parfois la langue brute, gotcha #166). */
function baseLanguage(language?: string): string {
    return (language || '').replace('_', '-').split('-')[0].toLowerCase();
}

/**
 * Playlists intégrées ordonnées par langue : la famille de la langue courante
 * d'abord (un bar anglais ne doit pas dérouler 11 playlists françaises avant
 * d'atteindre « Top UK »). Même règle que le sélecteur du jeu.
 */
function builtInPlaylistOptions(language?: string): SelectOption[] {
    const frenchFirst = baseLanguage(language) === 'fr';
    const fr = BLIND_TEST_PLAYLISTS.filter((p) => p.region === 'fr');
    const intl = BLIND_TEST_PLAYLISTS.filter((p) => p.region === 'intl');
    return (frenchFirst ? [...fr, ...intl] : [...intl, ...fr]).map(playlistOption);
}

/** Schémas par jeu (clé = gameID Firestore). */
export const GAME_PARAMS_SCHEMAS: Record<string, GameParamsSchema> = {
    'unblind-test': {
        gameID: 'unblind-test',
        titleKey: `${P}.titles.unblind-test`,
        icon: 'music',
        fields: [
            {
                type: 'select',
                key: 'playlist',
                labelKey: `${P}.fields.playlist`,
                options: builtInPlaylistOptions(),
                unsetLabelKey: `${P}.panel.asOnTv`,
            },
            // Même ordre et mêmes mots que l'écran du jeu (unblind-test GameSettings) :
            // playlist, ce que les joueurs devinent, comment ils répondent, combien de
            // titres, temps pour répondre. TOUS facultatifs (`unsetLabelKey`) : seul ce que l'animateur a
            // choisi ici part vers la TV, le reste suit le choix fait sur l'écran.
            {
                type: 'select',
                key: 'proposalMode',
                labelKey: `${P}.fields.guess`,
                // « L'œuvre » grisée sur une playlist intégrée sans films (la TV y joue l'artiste).
                options: (p) =>
                    PROPOSAL_MODES.map((m) => ({
                        value: m.value,
                        labelKey: m.labelKey,
                        ...(m.value === 'work' && !playlistAllowsWork(p.playlist)
                            ? {
                                  unavailable: {
                                      reasonKey: `${P}.proposalModes.workUnavailable`,
                                      summaryKey: `${P}.proposalModes.workPlayedAsArtist`,
                                  },
                              }
                            : {}),
                    })),
                unsetLabelKey: `${P}.panel.asOnTv`,
            },
            {
                type: 'select',
                key: 'answerMode',
                labelKey: `${P}.fields.answerMode`,
                options: ANSWER_MODES.map((m) => ({ value: m.value, labelKey: m.labelKey, newUntil: m.newUntil })),
                unsetLabelKey: `${P}.panel.asOnTv`,
            },
            {
                type: 'chips',
                key: 'numTracks',
                labelKey: `${P}.fields.numTracks`,
                // Playlist laissée à la TV : tous les paliers (le jeu borne de toute
                // façon au nombre de titres jouables de sa playlist).
                options: (p) => numTracksOptionsFor(String(p.playlist)),
                dependsOn: ['playlist'],
                unsetLabelKey: `${P}.panel.asOnTv`,
            },
            {
                type: 'select',
                key: 'reflectionTime',
                labelKey: `${P}.fields.reflectionTime`,
                options: REFLECTION_TIMES.map((r) => ({ value: r.value, labelKey: r.labelKey })),
                unsetLabelKey: `${P}.panel.asOnTv`,
            },
        ],
        // Replis de re-validation seulement : aucun champ du Blind Test n'est écrit par défaut.
        defaults: { ...DEFAULT_BLIND_TEST_PARAMS },
        note: { titleKey: `${P}.note.aiTitle`, subKey: `${P}.note.aiSub` },
    },

    geoloc: {
        gameID: 'geoloc',
        titleKey: `${P}.titles.geoloc`,
        icon: 'map-pin',
        fields: [
            { type: 'chips', key: 'numCities', labelKey: `${P}.fields.numCities`, options: [1, 5, 7, 10, 15, 20] },
            { type: 'select', key: 'theme', labelKey: `${P}.fields.theme`, options: GEOLOC_THEMES },
            { type: 'select', key: 'difficultyMode', labelKey: `${P}.fields.level`, options: DIFFICULTY_4 },
        ],
        defaults: { numCities: 7, theme: 'world-cities', difficultyMode: 'progressive' },
        // Ces deux thèmes forcent « easy » quel que soit le réglage (cf getDifficultyByRound,
        // geoloc/src/pages/game/screen/screen.tsx) : afficher un niveau y serait un bouton mort.
        hideField: (key, p) =>
            key === 'difficultyMode' && (p.theme === 'nba-cities' || p.theme === 'world-cup-countries'),
    },

    'petit-bac': {
        gameID: 'petit-bac',
        titleKey: `${P}.titles.petit-bac`,
        icon: 'pencil',
        fields: [
            { type: 'chips', key: 'numRounds', labelKey: `${P}.fields.numRounds`, options: [1, 3, 5, 10, 15] },
            {
                type: 'chips',
                key: 'numCategories',
                labelKey: `${P}.fields.numCategories`,
                options: [1, 4, 6, 8, 10],
            },
            {
                type: 'chips',
                key: 'durationByRound',
                labelKey: `${P}.fields.durationByRound`,
                options: [20, 30, 40, 50],
                format: seconds,
            },
            {
                // Écran des résultats entre deux lettres (15 s en dur avant, trop court
                // pour commenter les réponses — La Fourmilière, 22/10/2026).
                type: 'chips',
                key: 'resultDuration',
                labelKey: `${P}.fields.resultDuration`,
                options: [15, 30, 60],
                format: seconds,
            },
            {
                type: 'toggle',
                key: 'allowEarlyStop',
                labelKey: `${P}.fields.allowEarlyStop`,
                labelOnKey: `${P}.toggles.allowed`,
                labelOffKey: `${P}.toggles.disabled`,
            },
            {
                type: 'multiselect',
                key: 'availableCategories',
                labelKey: `${P}.fields.availableCategories`,
                options: PETIT_BAC_CATEGORIES,
                minSelected: 1,
            },
        ],
        defaults: {
            numRounds: 5,
            numCategories: 8,
            durationByRound: 40,
            resultDuration: 15,
            allowEarlyStop: true,
            availableCategories: PETIT_BAC_CATEGORIES.map((c) => c.value),
        },
    },

    estim: {
        gameID: 'estim',
        titleKey: `${P}.titles.estim`,
        icon: 'home',
        fields: [
            {
                type: 'select',
                key: 'country',
                labelKey: `${P}.fields.country`,
                // Les États-Unis existent côté jeu mais sont `comingSoon` (aucune annonce) :
                // les proposer ici donnerait une partie injouable.
                options: [
                    { value: 'FR', labelKey: `${P}.estim.france` },
                    { value: 'GB', labelKey: `${P}.estim.uk` },
                ],
            },
            {
                type: 'select',
                key: 'region',
                labelKey: `${P}.fields.region`,
                options: (p) => (p.country === 'GB' ? ESTIM_REGIONS_GB : ESTIM_REGIONS_FR),
            },
            {
                type: 'select',
                key: 'listingType',
                labelKey: `${P}.fields.listingType`,
                options: [
                    { value: 'buy', labelKey: `${P}.estim.buy` },
                    { value: 'rent', labelKey: `${P}.estim.rent` },
                ],
            },
            { type: 'chips', key: 'numRounds', labelKey: `${P}.fields.numRounds`, options: [5, 7, 10, 15] },
            {
                type: 'chips',
                key: 'roundDuration',
                labelKey: `${P}.fields.roundDuration`,
                options: [20, 30, 45],
                format: seconds,
            },
        ],
        defaults: { country: 'FR', region: 'all', listingType: 'buy', numRounds: 10, roundDuration: 30 },
    },

    'draw-guessr': {
        gameID: 'draw-guessr',
        titleKey: `${P}.titles.draw-guessr`,
        icon: 'pencil',
        fields: [
            { type: 'select', key: 'theme', labelKey: `${P}.fields.theme`, options: DRAW_GUESSR_THEMES },
            { type: 'select', key: 'difficulty', labelKey: `${P}.fields.difficulty`, options: DRAW_DIFFICULTY },
            { type: 'chips', key: 'numRounds', labelKey: `${P}.fields.numRounds`, options: [3, 4, 5, 7] },
            {
                type: 'chips',
                key: 'drawDuration',
                labelKey: `${P}.fields.drawDuration`,
                options: [30, 45, 60, 90],
                format: seconds,
            },
            {
                type: 'chips',
                key: 'guessDuration',
                labelKey: `${P}.fields.guessDuration`,
                options: [15, 25, 40],
                format: seconds,
            },
        ],
        defaults: { theme: 'melange', difficulty: 'progressive', numRounds: 4, drawDuration: 60, guessDuration: 25 },
    },

    footboared: {
        gameID: 'footboared',
        titleKey: `${P}.titles.footboared`,
        icon: 'goal',
        fields: [
            {
                type: 'chips',
                key: 'matchDuration',
                labelKey: `${P}.fields.matchDuration`,
                options: [60, 120, 180, 240, 300],
                format: (v, t) => t(`${P}.minutes`, { value: v / 60 }),
            },
            {
                type: 'chips',
                key: 'goalLimit',
                labelKey: `${P}.fields.goalLimit`,
                options: [3, 5, 7, 0],
                format: (v, t) => (v === 0 ? t(`${P}.noLimit`) : t(`${P}.goals`, { value: v })),
            },
            {
                type: 'toggle',
                key: 'botsActive',
                labelKey: `${P}.fields.bots`,
                labelOnKey: `${P}.toggles.botsOn`,
                labelOffKey: `${P}.toggles.botsOff`,
            },
        ],
        defaults: { matchDuration: 180, goalLimit: 3, botsActive: false },
    },

    untierce: {
        gameID: 'untierce',
        titleKey: `${P}.titles.untierce`,
        icon: 'sliders',
        fields: [
            {
                type: 'chips',
                key: 'totalRaces',
                labelKey: `${P}.fields.totalRaces`,
                // Recopie de RACE_COUNT_CHOICES (untierce). Le jeu borne de toute
                // façon la valeur reçue à 3–30, donc une divergence de cette liste
                // ne peut pas produire de réglage injouable.
                options: [3, 5, 8, 12, 20, 30],
                format: (v, t) => t(`${P}.races`, { value: v }),
            },
        ],
        defaults: { totalRaces: 3 },
    },

    'bomber-kitten': {
        gameID: 'bomber-kitten',
        titleKey: `${P}.titles.bomber-kitten`,
        icon: 'sliders',
        // Miroir de settingsFromAnimator (bomber-kitten/src/game/animatorParams.ts) et
        // des réglages de l'écran du jeu (2/3/4 min, arène). Le jeu applique les
        // réglages à la PROCHAINE partie et ignore toute valeur inconnue.
        fields: [
            {
                type: 'chips',
                key: 'matchDuration',
                labelKey: `${P}.fields.gameDuration`,
                options: [120, 180, 240],
                format: (v, t) => t(`${P}.minutes`, { value: v / 60 }),
            },
            {
                type: 'select',
                key: 'arena',
                labelKey: `${P}.fields.arena`,
                options: BOMBER_WORLDS,
            },
            {
                // Actifs = le jeu complète avec des bots tant qu'il y a moins de 4 joueurs.
                type: 'toggle',
                key: 'botsActive',
                labelKey: `${P}.fields.bots`,
                labelOnKey: `${P}.toggles.botsFill`,
                labelOffKey: `${P}.toggles.botsNone`,
            },
        ],
        defaults: { matchDuration: 180, arena: 'random', botsActive: true },
    },
};

/**
 * Valeurs par défaut qui dépendent de la langue de l'ÉTABLISSEMENT.
 *
 * Pourquoi c'est critique : `GameParamsPanel.apply` renvoie l'objet de réglages
 * COMPLET à chaque modification — défauts compris. Un défaut « français » part
 * donc vers le jeu dès que l'animateur touche un AUTRE réglage, et le jeu
 * l'applique tel quel. Constaté en relecture de console#166 : un bar anglais qui
 * changeait seulement le nombre de manches d'Estim basculait sur des annonces
 * françaises en euros (le jeu pose `countryTouched` et ne revient plus au
 * Royaume-Uni) ; au Blind Test, sur une playlist de chanson française.
 *
 * - Estim : même règle que le jeu (`defaultCountryForLanguage`, estim) —
 *   anglais → Royaume-Uni, tout le reste → France.
 * - Blind Test : hors français, une playlist internationale.
 */
function localizedDefaults(gameID: string, language?: string): ParamsMap {
    const lang = baseLanguage(language);
    if (gameID === 'estim') return { country: lang === 'en' ? 'GB' : 'FR' };
    if (gameID === 'unblind-test' && lang && lang !== 'fr') return { playlist: DEFAULT_INTL_PLAYLIST_ID };
    return {};
}

/** Options résolues d'un champ (statique ou dépendante des valeurs courantes). */
export function resolveOptions(field: ChipsField, p: ParamsMap): number[];
export function resolveOptions(field: SelectField, p: ParamsMap): SelectOption[];
export function resolveOptions(field: ChipsField | SelectField, p: ParamsMap): number[] | SelectOption[] {
    return typeof field.options === 'function' ? (field.options as (p: ParamsMap) => never)(p) : field.options;
}

/**
 * Valeur de repli d'un champ « chips » quand sa valeur n'est plus proposée — cas
 * réel : une playlist Blind Test plus courte réduit les nombres de chansons
 * possibles. Le défaut s'il est proposé (retour à une playlist normale → 15), sinon
 * l'option la PLUS PROCHE de la valeur courante (à égalité, la plus petite) :
 * 15 chansons sur une playlist IA de 12 titres → 12. Avant, le panneau retombait
 * sur la 1re option, soit 1 chanson, sans que l'animateur le remarque.
 */
export function fallbackChipValue(options: number[], current: unknown, fallback: number): number {
    if (options.length === 0) return fallback;
    if (options.includes(fallback)) return fallback;
    const target = typeof current === 'number' && Number.isFinite(current) ? current : fallback;
    if (options.includes(target)) return target;
    return options.reduce((best, o) => {
        const d = Math.abs(o - target);
        const bd = Math.abs(best - target);
        return d < bd || (d === bd && o < best) ? o : best;
    });
}

/** Champ facultatif (cf `SelectField.unsetLabelKey`) : écrit seulement s'il est choisi. */
export function isOptionalField(f: ParamField): boolean {
    return (f.type === 'select' || f.type === 'chips') && Boolean(f.unsetLabelKey);
}

/**
 * Réglages affichés à l'ouverture du panneau : défauts du schéma + ce qui est déjà
 * enregistré pour l'écran. Un champ facultatif jamais choisi reste ABSENT, même si
 * une valeur traîne dans les défauts.
 */
export function initialGameParams(schema: GameParamsSchema, saved?: ParamsMap | null): ParamsMap {
    const defaults = { ...schema.defaults };
    for (const f of schema.fields) if (isOptionalField(f)) delete defaults[f.key];
    return { ...defaults, ...(saved ?? {}) };
}

/**
 * Objet COMPLET écrit dans `gameParams` après un geste de l'animateur (`patch` = le
 * réglage touché). Re-valide chaque champ à options après le changement (les
 * options d'un champ peuvent dépendre d'un autre : nombre de titres ← playlist).
 * Un champ facultatif absent reste absent : il n'est jamais rempli par défaut.
 */
export function nextGameParams(schema: GameParamsSchema, params: ParamsMap, patch: ParamsPatch): ParamsMap {
    const next: ParamsMap = { ...params };
    for (const [key, value] of Object.entries(patch)) {
        // null = « Comme sur la TV » : la clé disparaît, le jeu garde son réglage.
        if (value === null) delete next[key];
        else next[key] = value;
    }
    // Réglage facultatif que ce geste ne touche pas, ni directement ni par ce dont
    // dépendent ses options (`dependsOn`) : gardé tel quel, même si ce téléphone ne le
    // connaît pas (playlist IA pas encore chargée, 12 titres d'une playlist perso) —
    // c'est celui de la TV. Changer la playlist re-valide bien le nombre de titres.
    const untouched = (f: ParamField) =>
        isOptionalField(f) && !(f.key in patch) && !(f.type === 'chips' && (f.dependsOn ?? []).some((k) => k in patch));
    for (const f of schema.fields.filter((field) => !untouched(field))) {
        if (f.type === 'chips') {
            // Facultatif laissé à la TV : rien à re-valider, rien à écrire.
            const leftToTv = isOptionalField(f) && next[f.key] === undefined;
            const opts = resolveOptions(f as ChipsField, next);
            if (!leftToTv && (typeof next[f.key] !== 'number' || !opts.includes(next[f.key] as number))) {
                // Valeur la plus proche, pas la 1re option (1 chanson) : cf fallbackChipValue.
                next[f.key] = fallbackChipValue(opts, next[f.key], schema.defaults[f.key] as number);
            }
        } else if (f.type === 'select') {
            const opts = resolveOptions(f as SelectField, next);
            const valid = opts.some((o) => o.value === next[f.key]);
            if (!valid && isOptionalField(f)) {
                // Jamais choisi (ou valeur devenue inconnue) : on n'envoie rien, la TV décide.
                delete next[f.key];
            } else if (!valid) {
                const def = schema.defaults[f.key];
                next[f.key] = opts.some((o) => o.value === def) ? def : (opts[0]?.value ?? def);
            }
        }
    }
    return next;
}

/**
 * Schéma du jeu, augmenté au besoin de données dynamiques propres au client et à
 * la langue courante.
 *
 * `language` = langue de l'ÉTABLISSEMENT (`client.language`), pas celle d'i18next :
 * le panneau fige ses valeurs initiales au premier rendu, avant que le cockpit
 * n'ait eu le temps de basculer i18next sur la langue du client.
 *
 * Trois cas aujourd'hui :
 *  - les valeurs par défaut suivent la langue (cf `localizedDefaults`) ;
 *  - Blind Test : les playlists IA sont persistées sur le compte client (donc
 *    inconnues du schéma statique) : on injecte la liste lue dans Firestore dans
 *    le sélecteur et on rebranche le bornage du « nombre de chansons » sur le
 *    nombre de pistes réel. Elles sont présentées en premier (l'animateur vient
 *    souvent d'en créer une) et gardent leur nom brut — c'est le client qui l'a
 *    écrit ;
 *  - Blind Test : les playlists intégrées sont ordonnées par famille selon la langue.
 */
export function schemaForGame(
    gameID: string,
    aiPlaylists: AiPlaylistOption[] = [],
    language?: string
): GameParamsSchema | undefined {
    const base = GAME_PARAMS_SCHEMAS[gameID];
    if (!base) return base;
    const defaults = { ...base.defaults, ...localizedDefaults(gameID, language) };
    if (gameID !== 'unblind-test') return { ...base, defaults };

    const fields = base.fields.map((f): ParamField => {
        if (f.type === 'select' && f.key === 'playlist') {
            const aiOptions: SelectOption[] = aiPlaylists.map((p) => ({
                value: p.id,
                label: `🎵 ${p.name}`,
                groupKey: `${P}.groupAi`,
            }));
            return { ...f, options: [...aiOptions, ...builtInPlaylistOptions(language)] };
        }
        if (f.type === 'chips' && f.key === 'numTracks' && aiPlaylists.length > 0) {
            return { ...f, options: (p: ParamsMap) => numTracksOptionsForPlaylist(String(p.playlist), aiPlaylists) };
        }
        return f;
    });

    return { ...base, defaults, fields };
}
